import React, {
  MutableRefObject,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { Divider, Tour, TourProps } from 'antd';
import { ArrowRightOutlined, CheckOutlined } from '@ant-design/icons';
import {
  selectCurrentUserId,
  selectSessionSelector,
} from '@mm/state/selectors/userSelector';
import { useSelector } from 'react-redux';
import { useUsersCRUDActions } from '@mm/state/actions/UsersCRUDActions';
import { selectActiveCompanyId } from '@mm/state/selectors/viewSelectors';
import capiImg from './capi-integration.png';
import fbCustomAudienceIng from './fb-custom-audience.png';
import styles from './RestoreTour.module.css';
import classNames from 'classnames';
import { Button } from '@mm/components';
import { useResolutionInfo } from '@mm/hooks';
import { MOBILE_THRESHOLD } from '../../../../../constants/screenSize';
import moment from 'moment';
import { TOURS_DATE_START_FROM } from '../../GetLeads/XRay/XRayLeadsTour';

export type RestoreTourRefs = {
  facebookCustomAudienceRef: MutableRefObject<HTMLButtonElement | null>;
};

type Props = { open?: boolean };

export const RestoreTour = React.forwardRef<RestoreTourRefs, Props>(
  (props, ref) => {
    const companyId = useSelector(selectActiveCompanyId);
    const currentUserId = useSelector(selectCurrentUserId);
    const session = useSelector(selectSessionSelector);
    const userAttributes = session?.userAttributes;
    const isNewUser = moment(session.createdAt) > moment(TOURS_DATE_START_FROM);
    const facebookCustomAudienceRef = useRef<HTMLButtonElement | null>(null);
    const helpDoc =
      'https://customers.ai/help/l/en/article/d0uzm8hcap-share-your-brand-s-meta-dataset-information-with-customers-ai';

    const { updateUserAttributes } = useUsersCRUDActions();

    const { screenWidth } = useResolutionInfo();
    const isMobile = screenWidth <= MOBILE_THRESHOLD;

    useImperativeHandle(ref, () => ({
      facebookCustomAudienceRef,
    }));

    const [open, setOpen] = useState<boolean>(false);
    const showTour =
      !userAttributes?.seenTour?.restore && !isMobile && isNewUser;

    const steps: TourProps['steps'] = [
      {
        title: <div className={styles.stepTitle}>Create Facebook Audience</div>,
        description: (
          <div className={styles.stepDescription}>
            Send more high intent data to Facebook ad for retargeting
            <Divider className={styles.divider} />
          </div>
        ),
        cover: (
          <img
            alt="Facebook Custom Audience"
            src={fbCustomAudienceIng}
            style={{ width: 'auto' }}
          />
        ),
        target: facebookCustomAudienceRef.current,
        placement: 'bottomLeft',
        nextButtonProps: {
          children: (
            <div>
              <ArrowRightOutlined /> Next
            </div>
          ),
          className: styles.nextButton,
        },
      },
      {
        title: (
          <div className={styles.stepTitle}>
            Send event data directly to Facebook's event manager
          </div>
        ),
        description: (
          <div className={styles.stepDescription}>
            Improve event match quality, lower ad costs, and improve ROA
            <Divider className={styles.divider} />
          </div>
        ),
        cover: (
          <img alt="CAPI Integration" src={capiImg} style={{ width: 'auto' }} />
        ),
        nextButtonProps: {
          children: (
            <div>
              <CheckOutlined /> Done
            </div>
          ),
          className: classNames(styles.nextButton, styles.doneButton),
        },
        prevButtonProps: {
          className: styles.prevButton,
          children: (
            <Button
              color="secondary-blue"
              css={{ container: styles.helpButton }}
            >
              Learn more
            </Button>
          ),
        },
        placement: 'bottomLeft',
        target: facebookCustomAudienceRef.current,
        onPrev: () => window.open(helpDoc, '_blank'),
      },
    ];

    useEffect(() => {
      if (showTour) {
        setOpen(true);
        updateUserAttributes(companyId, currentUserId, {
          seenTour: { ...userAttributes.seenTour, restore: true },
        });
      }
    }, []);

    if (!open) {
      return <></>;
    }

    return (
      <Tour
        rootClassName={styles.tour}
        open={open}
        onClose={() => setOpen(false)}
        indicatorsRender={(current, total) => (
          <span className={styles.indicator}>
            {current + 1} of {total}
          </span>
        )}
        steps={steps}
        mask
      />
    );
  },
);
